import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioSummary = ({ portfolioData, onNavigate }) => {
  const summaryCards = [
    {
      title: 'Total Credits',
      value: portfolioData?.totalCredits?.toLocaleString(),
      unit: 'tCO2e',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Briefcase',
      color: 'primary'
    },
    {
      title: 'High Integrity',
      value: portfolioData?.highIntegrityCredits?.toLocaleString(),
      unit: 'tCO2e',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Shield',
      color: 'success'
    },
    {
      title: 'CCP Approved',
      value: portfolioData?.ccpApprovedCredits?.toLocaleString(),
      unit: 'tCO2e',
      change: '+15.1%',
      changeType: 'positive',
      icon: 'CheckCircle',
      color: 'success'
    },
    {
      title: 'Available for Retirement',
      value: portfolioData?.availableCredits?.toLocaleString(),
      unit: 'tCO2e',
      change: '-3.2%',
      changeType: 'negative',
      icon: 'Archive',
      color: 'warning'
    }
  ];

  const registryBreakdown = [
    { name: 'Verra', count: 1250000, percentage: 45, color: 'bg-blue-500' },
    { name: 'Gold Standard', count: 850000, percentage: 31, color: 'bg-yellow-500' },
    { name: 'ACR', count: 420000, percentage: 15, color: 'bg-green-500' },
    { name: 'Others', count: 250000, percentage: 9, color: 'bg-gray-500' }
  ];

  const getChangeColor = (type) => {
    return type === 'positive' ? 'text-success' : 'text-error';
  };

  const getCardColor = (color) => {
    switch (color) {
      case 'primary': return 'border-primary/20 bg-primary/5';
      case 'success': return 'border-success/20 bg-success/5';
      case 'warning': return 'border-warning/20 bg-warning/5';
      default: return 'border-border bg-card';
    }
  };

  const getIconColor = (color) => {
    switch (color) {
      case 'primary': return 'text-primary';
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryCards?.map((card, index) => (
          <div key={index} className={`p-6 rounded-lg border ${getCardColor(card?.color)} transition-smooth hover:shadow-subtle`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`p-2 rounded-lg bg-background ${getIconColor(card?.color)}`}>
                <Icon name={card?.icon} size={20} />
              </div>
              <div className={`text-sm font-medium ${getChangeColor(card?.changeType)}`}>
                {card?.change}
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-muted-foreground">{card?.title}</h3>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-card-foreground">{card?.value}</span>
                <span className="text-sm text-muted-foreground">{card?.unit}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Registry Breakdown & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Registry Distribution */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-card-foreground">Registry Distribution</h3>
            <Button variant="ghost" size="sm" iconName="BarChart3">
              View Details
            </Button>
          </div>
          
          <div className="space-y-4">
            {registryBreakdown?.map((registry, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${registry?.color}`}></div>
                    <span className="font-medium text-card-foreground">{registry?.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-card-foreground">
                      {registry?.count?.toLocaleString()} tCO2e
                    </div>
                    <div className="text-xs text-muted-foreground">{registry?.percentage}%</div>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${registry?.color}`}
                    style={{ width: `${registry?.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-card-foreground mb-6">Quick Actions</h3>
          
          <div className="space-y-3">
            <Button 
              variant="default" 
              fullWidth 
              iconName="Plus"
              onClick={() => onNavigate('/claims-builder')}
            >
              Create New Claim
            </Button>
            
            <Button 
              variant="outline" 
              fullWidth 
              iconName="GitBranch"
              onClick={() => onNavigate('/provenance-tracking')}
            >
              Track Provenance
            </Button>
            
            <Button 
              variant="outline" 
              fullWidth 
              iconName="Link"
              onClick={() => onNavigate('/registry-connectors')}
            >
              Sync Registries
            </Button>
            
            <Button 
              variant="outline" 
              fullWidth 
              iconName="Download"
            >
              Export Portfolio
            </Button>
          </div>

          {/* Recent Activity */}
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="font-medium text-card-foreground mb-3">Recent Activity</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <div className="flex-1">
                  <div className="text-sm text-card-foreground">Credits verified</div>
                  <div className="text-xs text-muted-foreground">2 hours ago</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <div className="flex-1">
                  <div className="text-sm text-card-foreground">New credits added</div>
                  <div className="text-xs text-muted-foreground">5 hours ago</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-warning rounded-full"></div>
                <div className="flex-1">
                  <div className="text-sm text-card-foreground">Registry sync completed</div>
                  <div className="text-xs text-muted-foreground">1 day ago</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSummary;